import React from 'react';
import { ArrowRight } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { UI_Z_CLASS, UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  defaultStagePairsForPipelines,
  type StagePair,
} from '../../../utils/leadPipelineStageMap';
import type { LeadPipeline } from '../../../types';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';

export type PipelineStageMapChange = {
  categoryKey: string;
  categoryLabel: string;
  fromPipeline: LeadPipeline;
  toPipeline: LeadPipeline;
};

export type PipelineStageMapPayload = {
  category_key: string;
  from_pipeline_id: string;
  to_pipeline_id: string;
  stages: StagePair[];
};

type Props = {
  open: boolean;
  changes: PipelineStageMapChange[];
  onCancel: () => void;
  onConfirm: (maps: PipelineStageMapPayload[]) => void;
  confirming?: boolean;
};

function buildInitialMaps(changes: PipelineStageMapChange[]): Record<string, StagePair[]> {
  const out: Record<string, StagePair[]> = {};
  for (const c of changes) {
    out[c.categoryKey] = defaultStagePairsForPipelines(c.fromPipeline, c.toPipeline);
  }
  return out;
}

/** Counts per categoryKey → stageId. */
type StageCountMap = Record<string, Record<string, number>>;

export function PipelineStageMapModal({
  open,
  changes,
  onCancel,
  onConfirm,
  confirming = false,
}: Props) {
  const [mapsByCategory, setMapsByCategory] = React.useState<Record<string, StagePair[]>>(() =>
    buildInitialMaps(changes),
  );
  const [stageCounts, setStageCounts] = React.useState<StageCountMap>({});

  React.useEffect(() => {
    if (!open) return;
    setMapsByCategory(buildInitialMaps(changes));
  }, [open, changes]);

  React.useEffect(() => {
    if (!open || changes.length === 0) {
      setStageCounts({});
      return;
    }
    const categoryKeys = new Set(changes.map((c) => c.categoryKey));
    let cancelled = false;
    fetch('/api/leads', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const next: StageCountMap = {};
        for (const key of categoryKeys) next[key] = {};
        for (const row of Array.isArray(list) ? list : []) {
          const catKey = String(
            (row as { category_key?: unknown; category?: unknown }).category_key ??
              (row as { category?: unknown }).category ??
              '',
          ).trim();
          if (!catKey || !categoryKeys.has(catKey)) continue;
          const stageId = String((row as { stage_id?: unknown }).stage_id ?? '').trim();
          if (!stageId) continue;
          const byStage = next[catKey] ?? (next[catKey] = {});
          byStage[stageId] = (byStage[stageId] ?? 0) + 1;
        }
        setStageCounts(next);
      })
      .catch(() => {
        if (!cancelled) setStageCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, changes]);

  const setPair = (categoryKey: string, fromStageId: string, toStageId: string) => {
    setMapsByCategory((prev) => {
      const list = prev[categoryKey] ?? [];
      return {
        ...prev,
        [categoryKey]: list.map((p) =>
          p.from_stage_id === fromStageId ? { ...p, to_stage_id: toStageId } : p,
        ),
      };
    });
  };

  const canConfirm =
    changes.length > 0 &&
    changes.every((c) => {
      // Empty source/target pipelines: nothing to map — confirm clears/falls back server-side.
      if (c.fromPipeline.stages.length === 0 || c.toPipeline.stages.length === 0) return true;
      const pairs = mapsByCategory[c.categoryKey] ?? [];
      return c.fromPipeline.stages.every((s) =>
        pairs.some((p) => p.from_stage_id === s.id && Boolean(p.to_stage_id)),
      );
    });

  const handleConfirm = () => {
    if (!canConfirm) return;
    const payload: PipelineStageMapPayload[] = changes.map((c) => ({
      category_key: c.categoryKey,
      from_pipeline_id: c.fromPipeline.id,
      to_pipeline_id: c.toPipeline.id,
      stages: mapsByCategory[c.categoryKey] ?? [],
    }));
    onConfirm(payload);
  };

  return (
    <AppModal
      open={open}
      onClose={onCancel}
      disableClose={confirming}
      closeOnOverlayClick={!confirming}
      overlayClassName={UI_Z_CLASS.plantafelDrawerModal}
      panelClassName="md:max-w-xl"
      ariaLabelledBy="pipeline-stage-map-title"
    >
      <div className="flex flex-col min-h-0 h-full md:h-auto">
        <div className="px-5 pt-5 pb-4 border-b border-app-border shrink-0">
          <h2 id="pipeline-stage-map-title" className="text-base font-semibold text-app-text">
            Phasen übersetzen
          </h2>
          <p className="mt-1.5 text-sm text-app-text-subtle leading-snug">
            Bestehende Projekte werden auf die neue Pipeline verschoben. Ordne jeder bisherigen
            Phase eine Zielphase zu.
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4 app-scrollbar">
          {changes.map((change) => {
            const pairs = mapsByCategory[change.categoryKey] ?? [];
            const toOptions = change.toPipeline.stages.map((s) => ({
              value: s.id,
              label: s.label,
            }));
            return (
              <section key={change.categoryKey} className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-app-text">
                    {change.categoryLabel || change.categoryKey}
                  </p>
                  <p className="mt-1 text-xs text-app-text-subtle">
                    {change.fromPipeline.name}
                    <span className="mx-1.5 text-slate-300">→</span>
                    {change.toPipeline.name}
                  </p>
                </div>

                {change.fromPipeline.stages.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                    Die bisherige Pipeline hat keine Phasen.
                  </p>
                ) : change.toPipeline.stages.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                    Die neue Pipeline hat keine Phasen.
                  </p>
                ) : (
                  <div>
                    <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] gap-2 px-0.5 pb-2">
                      <span className={SETTINGS_TABLE_HEADER}>Bisher</span>
                      <span aria-hidden />
                      <span className={SETTINGS_TABLE_HEADER}>Neu</span>
                    </div>
                    <ul className="divide-y divide-app-border border-y border-app-border">
                      {change.fromPipeline.stages.map((fromStage) => {
                        const pair = pairs.find((p) => p.from_stage_id === fromStage.id);
                        const value = pair?.to_stage_id ?? toOptions[0]?.value ?? '';
                        const count = stageCounts[change.categoryKey]?.[fromStage.id] ?? 0;
                        const labelWithCount = `${fromStage.label} (${count})`;
                        return (
                          <li
                            key={fromStage.id}
                            className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] gap-1.5 sm:gap-2 sm:items-center py-2.5"
                          >
                            <div className="min-w-0">
                              <span className={`sm:hidden mb-0.5 block ${SETTINGS_TABLE_HEADER}`}>
                                Bisher
                              </span>
                              <span
                                className="block truncate text-sm font-normal text-app-text-muted"
                                title={labelWithCount}
                              >
                                {fromStage.label}
                                <span className="text-slate-400"> ({count})</span>
                              </span>
                            </div>
                            <div className="hidden sm:flex items-center justify-center text-slate-300">
                              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                            </div>
                            <div className="min-w-0">
                              <span className={`sm:hidden mb-0.5 block ${SETTINGS_TABLE_HEADER}`}>
                                Neu
                              </span>
                              <AppSelectDropdown
                                value={value}
                                onChange={(v) => setPair(change.categoryKey, fromStage.id, v)}
                                options={toOptions}
                                searchable={toOptions.length > 6}
                                searchPlaceholder="Phase suchen"
                                className="w-full min-w-0"
                                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-app-border flex items-center justify-end gap-2 shrink-0 bg-app-surface">
          <button
            type="button"
            onClick={onCancel}
            disabled={confirming}
            className={SETTINGS_BTN_OUTLINE}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirming || !canConfirm}
            className={SETTINGS_BTN_PRIMARY}
          >
            {confirming ? 'Speichert…' : 'Übersetzen und speichern'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}
